import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiLock, FiEdit2, FiTrash2 } from "react-icons/fi";
import { ResumoPreInscricao } from "components/ui/ResumoPreInscricao";
import { useActivePhases, usePeriodoFase } from "hooks/useControlDate";
import { api } from "services/api";
import { COLECAO_INICIATIVAS, PHASE_IDS } from "utils/constants";
import { formatarPeriodo } from "utils/cronograma";
import { ROTA_PRE_INSCRICAO } from "utils/destino-inicial";
import { getRotuloSemestre } from "utils/direcionadores";
import { montarExclusaoPreInscricao } from "utils/pre-inscricao-payload";

interface CardIniciativaConexaoProps {
  entrega: any;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate?: () => void;
  currentUserId?: string;
}

export function CardIniciativaConexao({
  entrega,
  index,
  isExpanded,
  onToggle,
  onUpdate,
  currentUserId,
}: CardIniciativaConexaoProps) {
  const phases = useActivePhases();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => descriptionRef.current?.focus(), 50);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (showDeleteModal) {
      setTimeout(() => modalRef.current?.focus(), 50);
    }
  }, [showDeleteModal]);

  const dentroPeridoCadastro = phases.conexaoPreRegistration;
  const periodoValidacao = formatarPeriodo(usePeriodoFase(PHASE_IDS.CONEXAO_MANAGER_APPROVAL));
  const periodoInscricao = formatarPeriodo(usePeriodoFase(PHASE_IDS.CONEXAO_REGISTRATION_PERIOD));

  const handleEdit = () => {
    navigate(ROTA_PRE_INSCRICAO, { state: { editData: entrega } });
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setDeleteSuccess(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      await api.put(`/database/${COLECAO_INICIATIVAS}`, montarExclusaoPreInscricao(entrega));
      setDeleteSuccess(true);
      setTimeout(() => {
        setShowDeleteModal(false);
        setDeleteSuccess(false);
        if (onUpdate) {
          onUpdate();
        }
      }, 2000);
    } catch (error) {
      console.error("Erro ao inativar iniciativa:", error);
      alert("Erro ao inativar a iniciativa. Tente novamente.");
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteSuccess(false);
  };
  
  return (
    <div className="border border-gray-200 rounded-lg p-4 sm:p-5 lg:p-6 bg-white">
      <div className="space-y-2.5 sm:space-y-3">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
          <div className="flex-1 w-full sm:w-auto" tabIndex={0}>
            <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">
              Nome da iniciativa:
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm break-words">{entrega.title}</p>
          </div>
          {dentroPeridoCadastro && !phases.conexaoManagerApproval && currentUserId === entrega.owner && (
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={handleEdit}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-2 text-xs sm:text-sm text-blue-600 hover:text-blue-700 active:text-blue-800 hover:bg-blue-50 rounded transition-colors touch-manipulation"
                title="Editar iniciativa"
              >
                <FiEdit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Editar
              </button>
              <button
                onClick={handleDeleteClick}
                disabled={isDeleting}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-3 py-2 text-xs sm:text-sm text-red-600 hover:text-red-700 active:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50 touch-manipulation"
                title="Excluir iniciativa"
              >
                <FiTrash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {isDeleting ? "Excluindo..." : "Excluir"}
              </button>
            </div>
          )}
        </div>

        {!isExpanded && (
          <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-8">
            <div>
              <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Direcionador transversal:</h3>
              <p className="text-gray-600 text-xs sm:text-sm">{entrega.direcionador?.nome ?? "—"}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Em qual semestre?</h3>
              <p className="text-gray-600 text-xs sm:text-sm">{getRotuloSemestre(entrega.semestre) ?? "—"}</p>
            </div>
          </div>
        )}

        {isExpanded && (
          <div className="pt-3 sm:pt-4 space-y-3 sm:space-y-4">
            <div ref={descriptionRef} tabIndex={-1} className="outline-none">
              <ResumoPreInscricao iniciativa={entrega} exibirNome={false} />
            </div>

            {/* Agenda de aprovação */}
            <div className="bg-purple-100 rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-purple-900 mb-3 text-sm sm:text-base" tabIndex={0}>
                Agenda de aprovação
              </h3>
              <div className="bg-white rounded-lg p-3 sm:p-4">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <FiLock className="w-4 h-4 text-gray-500" />
                      <p className="text-xs sm:text-sm text-gray-700" tabIndex={0}>
                        Validação pela {entrega.unidade_validadora?.nome ?? "unidade responsável"}
                      </p>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${
                          entrega.status === "aprovada"
                            ? "bg-green-bb-2"
                            : entrega.status === "reprovada"
                            ? "bg-red-500"
                            : "bg-gray-300"
                        }`}
                        style={{ width: entrega.status === "aprovada" || entrega.status === "reprovada" ? "100%" : "0%" }}
                      ></div>
                    </div>
                    {entrega.status === "aprovada" ? (
                      <div className="mt-1 space-y-3">
                        <p className="text-xs text-green-bb-2 font-semibold">
                          Validada
                        </p>

                        {/* Próxima etapa: inscrição */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3" tabIndex={0}>
                          <p className="text-xs font-semibold text-green-800 mb-1">
                            Próxima etapa, complete sua inscrição
                            {periodoInscricao && (
                              <span className="font-normal text-green-700"> — de {periodoInscricao}</span>
                            )}
                          </p>
                          <ul className="text-xs text-green-800 space-y-1 list-disc list-inside">
                            <li>Publique um vídeo sobre a iniciativa</li>
                            <li>Dê aceite no regulamento</li>
                            <li>Todos os participantes devem confirmar a participação</li>
                          </ul>
                        </div>
                      </div>
                    ) : entrega.status === "reprovada" ? (
                      <p className="text-xs text-red-500 font-semibold mt-1">
                        Reprovada
                      </p>
                    ) : (
                      <p className="text-xs text-pink-bb-1 mt-1">
                        Aguardando validação{periodoValidacao && `: de ${periodoValidacao}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={onToggle}
        className="mt-3 sm:mt-4 text-blue-600 font-semibold hover:text-blue-700 active:text-blue-800 transition-colors flex items-center gap-2 text-xs sm:text-sm touch-manipulation w-full sm:w-auto justify-center sm:justify-start py-2 sm:py-0"
      >
        <FiChevronDown
          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
        {isExpanded ? "VER MENOS INFORMAÇÕES" : "VER MAIS INFORMAÇÕES"}
      </button>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Confirmar exclusão de iniciativa" className="bg-white rounded-lg p-5 sm:p-6 max-w-md w-full shadow-xl outline-none">
            {deleteSuccess ? (
              <>
                <h3 className="text-base sm:text-lg font-bold text-green-600 mb-3 sm:mb-4">
                  Sucesso!
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Iniciativa excluída com sucesso!
                </p>
              </>
            ) : (
              <>
                <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">
                  Confirmar Exclusão
                </h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Tem certeza que deseja excluir esta iniciativa? Esta ação não poderá ser desfeita.
                </p>
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={isDeleting}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm sm:text-base text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition-colors font-semibold disabled:opacity-50 touch-manipulation"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm sm:text-base text-white bg-red-600 rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-semibold disabled:opacity-50 touch-manipulation"
                  >
                    {isDeleting ? "Excluindo..." : "Confirmar Exclusão"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
